import { apiDomain } from "../../main";

/**
 * Map steps to one format
 * @param steps
 * @param input
 * @param type
 * @returns {*}
 */
export function mapSteps(steps, input, type = 'main') {
  // Extract the input type from the input ID
  let inputType = '';
  if (type === 'main') {
    inputType = input.id.slice(5, -1); // Extract the input type without the suffix
    return steps.map(step => ({
      fromInput: step['from' + inputType.charAt(0).toUpperCase() + inputType.slice(1)],
      toInput: step['to' + inputType.charAt(0).toUpperCase() + inputType.slice(1)],
      step: step.step,
    }));
  } else {
    inputType = input.id.slice(12);
    return steps.map(step => ({
      fromInput: step['from' + inputType],
      toInput: step['to' + inputType],
      step: step.step,
    }));
  }
}

/**
 * Init steps
 * @param mappedSteps
 * @param range
 * @param defaultValue
 */
export function initSteps(mappedSteps, range, defaultValue) {
  if (mappedSteps && range) {
    mappedSteps.forEach(function (step) {
      if (defaultValue >= step.fromInput && defaultValue < step.toInput + 1) {
        range.step = step.step;
      }
    });
  }
}

/**
 * Fetch from endpoint and run function to set that data to fields
 * @param apiDomain
 * @param amount
 * @param period
 * @param firstPaymentDate
 * @param context
 * @param newDate
 * @param currentState
 */
export function fetchDataAndUpdateFields(apiDomain, amount, period, firstPaymentDate, context, newDate = null, currentState) {
  // Perform the fetch request
  return new Promise((resolve, reject) => {
    fetch(`${apiDomain}/loan-calculator?amount=${amount}&period=${period}&firstrepaymentdate=${firstPaymentDate}`)
      .then(response => {
        // Check if the request was successful (status code 200 OK)
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        // Parse the response body as JSON
        return response.json();
      })
      .then(data => {
        const result = setLoanData(context, data, firstPaymentDate, newDate, amount, period, currentState);
        resolve(result);
      })
      .catch(error => {
        // Handle errors during the fetch
        console.error('Fetch error:', error);
        reject(error);
      });
  })
}

/**
 * Debounce fetch function
 * @param func
 * @param delay
 * @returns {function(): Promise}
 */
function debounce(func, delay) {
  let timeoutId;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(timeoutId);
    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(function () {
        try {
          const result = func.apply(context, args);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
}

export const debouncedFetchDataAndUpdateFields = debounce(fetchDataAndUpdateFields, 500);

/**
 * Set info fields with fetch data
 * @param context
 * @param response
 * @param firstPaymentDate
 * @param newDate
 * @param amount
 * @param period
 * @param currentState
 * @returns {{currentBtnLoanLink: string, formLink: string}}
 */
export function setLoanData(context, response, firstPaymentDate, newDate, amount, period, currentState) {
  //Installment From
  setAmountField(context.calculatorFields.installmentAmount, response.data.installmentAmount.toFixed(0));
  if (context.calculatorFields.rangeInstallments) {
    checkIfInstallmentLower(parseInt(context.calculatorFields.inputInstallments.value), response.data.installmentAmount, context.calculatorFields.lowerInstallment);
  }

  // RRSO
  if (context.calculatorFields.rrso) {
    context.calculatorFields.rrso.innerHTML =
      response.data.actualAnnualInterestRate.toFixed(2).replace('.', ',') + '<span' +
      ' class="percent-symbol"> %</span>';
  }

  //Interest
  setAmountField(context.calculatorFields.totalAmountInterest, response.data.totalAmountInterest);
  if (context.calculatorFields.totalAmountInterestMobile) {
    setAmountField(context.calculatorFields.totalAmountInterestMobile, response.data.totalAmountInterest);
  }

  //Preview calc interest
  if (context.calculatorFields.interest) {
    context.calculatorFields.interest.textContent = formatAndRoundNumber(currentState.currentOffer.annualInterestsRate);
  }

  //Commission
  setAmountField(context.calculatorFields.totalCommissions, response.data.totalCommissions);
  if (context.calculatorFields.totalCommissionsMobile) {
    setAmountField(context.calculatorFields.totalCommissionsMobile, response.data.totalCommissions);
  }

  //Preview calc commission
  if (context.calculatorFields.commission) {
    setAmountField(context.calculatorFields.commission, response.data.totalCommissions);
  }

  //Amount to pay
  setAmountField(context.calculatorFields.totalAmountToPay, response.data.totalAmountToPay);
  if (context.calculatorFields.totalAmountToPayMobile) {
    setAmountField(context.calculatorFields.totalAmountToPayMobile, response.data.totalAmountToPay);
  }

  //Payment date
  let dateObject = new Date(firstPaymentDate);
  if (newDate) {
    dateObject = new Date(newDate);
  }
  const payDay = dateObject.getDate();
  context.calculatorFields.paymentDate.textContent = payDay + " dzień mies.";
  if (context.calculatorFields.paymentDateMobile) {
    context.calculatorFields.paymentDateMobile.textContent = payDay + " dzień mies.";
  }

  return {
    currentBtnLoanLink: `/wnioskuje?amount=${amount}&period=${period}&firstRepaymentDate=${firstPaymentDate}`,
    formLink: `/loan-calculator/information?amount=${amount}&period=${period}&installment=${response.data.installmentAmount}&interestOnCapital=${response.data.interestOnCapital}&interestOnCommission=${response.data.interestOnCommission}&provision=${response.data.totalCommissions}&totalAmountToPay=${response.data.totalAmountToPay}&annualPercentageRate=${response.data.actualAnnualInterestRate}`,
  };
}

/**
 * Format the amount to the correct display format
 * @param field
 * @param value
 */
export function setAmountField(field, value) {
  if (field) {
    field.innerHTML = value.toLocaleString('fr-FR') + '<span class="currency-symbol"> zł</span>'; //Fr because
    // thousands need spaces (ex. "2100" needs to be "2 100" in PL it will be "2100")
  }
}

/**
 * Format number to two decimal numbers
 * @param number
 * @returns {string}
 */
function formatAndRoundNumber(number) {
  // Multiply by 100 to convert to percentage
  number = number * 100;
  // Format number to 2 decimal places
  let formattedNumber = number.toFixed(2).toString();
  formattedNumber = formattedNumber.replace('.', ',');
  return formattedNumber + ' %';
}

/**
 * Format date to correct display format
 * @param inputDateString
 * @returns {string}
 */
export function formatDate(inputDateString) {
  const inputDate = new Date(inputDateString);
  const year = inputDate.getFullYear();
  const month = String(inputDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(inputDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 *
 * @param calcType
 * @param input
 * @param context
 * @param currentState
 * @param siteUrl
 * @param formLinks
 */
export function addDateEventListener(calcType, input, context, currentState, siteUrl, formLinks) {
  input.addEventListener('change', event => {
    currentState.currentDate = event.target.value;
    let amount = null;
    if (calcType === document.getElementById('calculator')) {
      amount = currentState.currentAmountValue;
    }
    if (calcType === document.getElementById('preview-calculator')) {
      amount = currentState.currentAmountLeftValue + currentState.currentCashValue;
    }
    fetchDataAndUpdateFields(apiDomain, amount, currentState.currentPeriodValue, currentState.currentDate, context)
      .then(buttonLinks => {
        currentState.currentBtnLoanLink = siteUrl + buttonLinks.currentBtnLoanLink;
        formLinks.formLink.href = apiDomain + buttonLinks.formLink;
        formLinks.formLinkMobile.href = apiDomain + buttonLinks.formLink;
      })
      .catch(error => {
        console.error('Error fetching data and updating fields:', error);
      });

  })
}

/**
 * Update range progress bar color
 * @param rangeValue
 * @param range
 * @param min
 * @param max
 * @param colors
 */
export function updateRangeBarColor(rangeValue, range, min, max, colors) {
  const progress = Math.ceil(((rangeValue - min) / (max - min)) * 100);
  range.style.background = `linear-gradient(to right, ${colors.leftSide} ${progress}%, ${colors.rightSide} ${progress}%)`;
}

/**
 * Move to calculator form after button click
 * @param button
 * @param currentState
 */
export function initLoanBtnListener(button, currentState) {
  button.addEventListener('click', function () {
    window.location.href = currentState.currentBtnLoanLink;
  })
}

/**
 * Checks whether the value is a step and requires correction.
 * @param value
 * @param steps
 * @returns {number|*}
 */
export function checkIfValueIsStep(value, steps) {
  for (const step of steps) {
    if (value >= step.fromInput && value <= step.toInput) {
      const remainder = value % step.step;
      return remainder !== 0 ? Math.round(value / step.step) * step.step : value;
    }
  }
}

/**
 *  If current state of input value is equal to min value of that field, add min attribute to that field
 *  in other case remove it.
 * @param input
 * @param currentFieldValue
 * @param minFieldValue
 * @param rangeField
 */
export function ifValueIsMin(input, currentFieldValue, minFieldValue, rangeField) {
  if (currentFieldValue === minFieldValue) {
    addMinAttribute(rangeField, minFieldValue);
  } else {
    if (rangeField.hasAttribute('min')) removeMinAttribute(rangeField);
  }
}

/**
 * Add min attribute to range field
 * @param rangeInput
 * @param min
 */
function addMinAttribute(rangeInput, min) {
  rangeInput.setAttribute('min', min);
}

/**
 * Remove min attribute from range field
 * @param rangeInput
 */
function removeMinAttribute(rangeInput) {
  rangeInput.removeAttribute('min');
}

/**
 * Format value to currency
 * @param value
 * @returns {string}
 */
export function formatToCurrency(value) {
  return value.toLocaleString('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

/**
 * Format the value to a format with a space after the thousands
 * @param value
 * @returns {string}
 */
export function formatNumber(value) {
  return value.toLocaleString('fr-FR');
}

/**
 * Check if input value isn't > max & < min
 * @param input
 * @param inputValue
 * @param inputRange
 * @param currentState
 * @param previousValue
 * @param inputMin
 * @param inputMax
 * @param rangeBarColors
 * @returns {Promise}
 */
export function handleAmountInput(input, inputValue, inputRange, currentState, previousValue, inputMin, inputMax, rangeBarColors) {
  return new Promise((resolve, reject) => {
    try {
      if (inputValue !== previousValue) {
        previousValue = inputValue;
        if (inputValue < inputMin) {
          setValueAndState(input, currentState, inputMin);
          inputRange.value = inputMin;
          updateRangeBarColor(inputMin, inputRange, inputMin, inputMax, rangeBarColors);
        } else if (inputValue > inputMax) {
          setValueAndState(input, currentState, inputMax);
          inputRange.value = inputMax;
          updateRangeBarColor(inputMax, inputRange, inputMin, inputMax, rangeBarColors);
        } else {
          setValueAndState(input, currentState, inputValue);
          inputRange.value = inputValue;
          updateRangeBarColor(inputValue, inputRange, inputMin, inputMax, rangeBarColors);
        }
        resolve({ previousValue });
      }
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Set input value and update current state
 * @param input
 * @param currentState
 * @param inputValue
 */
function setValueAndState(input, currentState, inputValue) {
  switch (input.id) {
    case 'inputInstallments':
      input.value = formatToCurrency(inputValue);
      currentState.currentInstallmentValue = inputValue;
      break;
    case 'inputAmountLeft':
      input.value = formatToCurrency(inputValue);
      currentState.currentAmountLeftValue = inputValue;
      break;
    case 'inputCash':
      input.value = formatToCurrency(inputValue);
      currentState.currentCashValue = inputValue;
      break;
    case 'inputPreviewPeriod':
      input.value = inputValue + ' mies.';
      currentState.currentPeriodValue = inputValue;
      break;
  }
}

/**
 * Check whether the proposed installment is lower than the user's installment
 * @param userInstallment
 * @param calculatedInstallment
 * @param lowerInstallmentField
 */
export function checkIfInstallmentLower(userInstallment, calculatedInstallment, lowerInstallmentField) {
  const lwrInstWrapper = document.getElementById('lower-installment-wrapper');
  const lwrInstLabels = document.getElementById('lower-installment-labels');
  const installmentCrossed = document.getElementById('installmentCrossed');

  if (userInstallment > calculatedInstallment) {
    let lwrInstVal = parseFloat(userInstallment - calculatedInstallment);

    lowerInstallmentField.innerHTML = '<span class="line-through">' + lwrInstVal.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }) + '</span><span class="currency-symbol">zł</span>';
    lwrInstWrapper.style.display = 'flex';
    lwrInstLabels.style.display = 'flex';
    installmentCrossed.innerHTML = '<span class="line-through">' + userInstallment.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }) + '</span><span class="currency-symbol">zł</span>';
    installmentCrossed.style.display = 'flex';
  } else {
    lwrInstWrapper.style.display = 'none';
    lwrInstLabels.style.display = 'none';
    installmentCrossed.style.display = 'none';
  }
}
